import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { PermissionsEnum } from '@/auth/interfaces';
import { gridSizeMdLg6, ROUTER_PATHS, useCheckPermissions } from '@/shared';
import { useFetchCountries } from '@/shared/actions/erp/countries.actions';
import {
  CreateProvinceParams,
  useCreateProvince,
  useUpdateProvince,
} from '@/shared/actions/erp/provinces.actions';
import {
  CustomAutocomplete,
  CustomTextField,
  SampleCheckbox,
  SingleFormBoxScene,
} from '@/shared/components/ui';
import { Country, Province } from '@/shared/interfaces/erp/system-settings';
import { newProvinceSchema } from '@/shared/utils';

export type SaveProvinceProps = {
  editProvince?: Province;
  titlePage: string;
};

export type SaveFormData = CreateProvinceParams & {};

const SaveProvince: React.FC<SaveProvinceProps> = ({
  titlePage,
  editProvince,
}) => {
  useCheckPermissions(PermissionsEnum.api_view_pais);
  const returnUrlProvinces = ROUTER_PATHS.administration.provinciasNav;

  const navigate = useNavigate();
  const [shrink, setShrink] = useState(false);

  ///* fetch data
  const { data: countriesPaginRes, isLoading } = useFetchCountries();

  ///* mutations
  const createProvinceMutation = useCreateProvince({
    navigate,
    returnUrl: returnUrlProvinces,
    enableErrorRedirect: false,
  });
  const updateProvinceMutation = useUpdateProvince({
    navigate,
    returnUrl: returnUrlProvinces,
    enableErrorRedirect: false,
  });

  ///* form
  const form = useForm<SaveFormData>({
    resolver: yupResolver(newProvinceSchema) as any,
    defaultValues: {
      estado: true,
      cobertura: false,
    },
  });
  const {
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = form;

  ///* effects
  useEffect(() => {
    if (!editProvince?.id_provincia) return;
    setShrink(true);
    reset(editProvince);
  }, []);

  ///* handlers
  const onSave = () => {
    if (!isValid) return;

    ///* edit
    if (editProvince?.id_provincia) {
      return updateProvinceMutation.mutate({
        id: editProvince.id_provincia,
        data: {
          ...form.getValues(),
        },
      });
    }

    ///* create

    return createProvinceMutation.mutate({
      ...form.getValues(),
    });
  };

  return (
    <SingleFormBoxScene
      titlePage={titlePage}
      onCancel={() => {
        navigate(returnUrlProvinces);
      }}
      onSave={handleSubmit(onSave)}
    >
      <CustomTextField
        label="Nombre"
        name="nombre"
        control={form.control}
        defaultValue={form.getValues().nombre || ''}
        error={errors.nombre}
        helperText={errors.nombre?.message ?? ''}
        shrink={shrink}
      />

      {/* <CustomTextField
        label="Código"
        name="codigo"
        control={form.control}
        defaultValue={form.getValues().codigo || ''}
        error={errors.codigo}
        helperText={errors.codigo?.message ?? ''}
        size={gridSizeMdLg6}
        disabled={!!editProvince?.id_provincia}
        shrink={shrink}
      /> */}

      {/* ====== select country ====== */}
      <CustomAutocomplete<Country>
        label="País"
        name="pais"
        // options
        options={countriesPaginRes?.data.data || []}
        valueKey="nombre"
        actualValueKey="codigo" // code is the fk
        defaultValue={form.getValues().pais || ''}
        optionLabelForEdit={form.getValues().pais || ''}
        isLoadingData={isLoading}
        // validation
        control={form.control}
        error={errors.pais}
        helperText={errors.pais?.message}
        size={gridSizeMdLg6}
      />

      <SampleCheckbox
        label="Tiene cobertura"
        name="cobertura"
        control={form.control}
        defaultValue={!!form.getValues().cobertura}
        size={gridSizeMdLg6}
      />
      <SampleCheckbox
        label="Estado"
        name="estado"
        control={form.control}
        defaultValue={!!form.getValues().estado}
        isState
        size={gridSizeMdLg6}
      />
    </SingleFormBoxScene>
  );
};

export default SaveProvince;
